'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetUserPnlChartRow, useActiveWallet, useGetUserPnlChart } from '@morfi/backend-sdk-v2/client';
import { isI64, isSignedMagnitudeU128 } from '@morfi/market-math';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { GiftButton } from '@/components/gift/gift-button';
import { FundingEntry } from '@/components/onramp/funding-entry';
import { PnlCardLiveline, type PnlLivelinePoint } from '@/components/portfolio/cards/pnl-card-liveline';
import { PnlChartCard } from '@/components/portfolio/cards/pnl-chart-card';
import { PortfolioCurrentSummary } from '@/components/portfolio/cards/portfolio-current-summary';
import { useRefetchWhenVisible } from '@/components/portfolio/cards/use-refetch-when-visible';
import { useResolvedSelection } from '@/components/portfolio/header/use-resolved-selection';
import { truncateAddress } from '@/components/portfolio/portfolio-helpers';
import { WalletQrDialog } from '@/components/portfolio/wallet-qr-dialog';
import { AnimatedButton } from '@/components/ui/animated-button';
import type { DropdownEntry } from '@/components/ui/app-dropdown-menu';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { ArrowDown03Icon, ArrowUpRight01Icon, ExpandIcon } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { PulseDot } from '@/components/ui/pulse-dot';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { UnavailableWalletBadge } from '@/components/ui/unavailable-wallet-badge';
import { D6_SCALE } from '@/config/solana';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { usePortfolioWalletSelectionStore } from '@/stores/portfolio-wallet-selection';
import type { Wallet } from '@/types/auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PortfolioPnlCardProps {
  wallets: ReadonlyArray<Wallet>;
  registeredAt: string | null;
  viewerMode: 'own' | 'other';
  userId: string;
  /** Query identity owned by the profile portfolio snapshot provider. */
  snapshotUserId: string;
  /**
   * Single wallet pubkey — used to KEY the holdings/pnl REST fetches
   * (single-wallet mode) but NOT for the realtime subscription identity.
   * Realtime threads `walletPubkeys` (array) instead so subscriptions
   * cover EVERY wallet.
   */
  walletPubkey: string;
  /**
   * Every wallet pubkey the user owns. Threaded as `walletPubkeys:`
   * (array) into per-user-feed hooks so realtime subscriptions cover
   * EVERY wallet (a single-wallet subscription silently misses the rest).
   */
  walletPubkeys: readonly string[];
}
type PortfolioPerformance = NonNullable<GetUserPnlChartRow['portfolioPerformance']>;
type PnlLinePoint = PortfolioPerformance['line'][number];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Map the portfolio-performance history to the sparkline's point shape. Its
 * open leg is the server's five-minute estimate, so the line and the delta
 * below it move together but neither reconciles to the last cent with the
 * Total above, which marks live against pool depth. Number conversion below is
 * only for the display-only sparkline after signed-I64 validation.
 */
function adaptPerformanceSeries(line: ReadonlyArray<PnlLinePoint> | undefined): ReadonlyArray<PnlLivelinePoint> | null {
  if (line === undefined || line.length < 2) return null;
  const points = line.map((point): PnlLivelinePoint | null => {
    const valueD6 = signedI64Wire(point.value);
    if (valueD6 === null) return null;
    return {
      time: new Date(point.timestamp).getTime() / 1000,
      value: Number(valueD6) / D6_SCALE
    };
  });
  return points.every((point): point is PnlLivelinePoint => point !== null) ? points : null;
}
function signedI64Wire(value: string | undefined): bigint | null {
  if (value === undefined || !/^(?:0|-?[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isI64(parsed) ? parsed : null;
}
function signedAggregateD6Wire(value: string | undefined): bigint | null {
  if (value === undefined || !/^(?:0|-?[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isSignedMagnitudeU128(parsed) ? parsed : null;
}

// ---------------------------------------------------------------------------
// Internal subcomponents
// ---------------------------------------------------------------------------

interface PnlCardActionsProps {
  onDeposit: () => void;
  onWithdraw: () => void;
  onGiftClickBefore: () => void;
}
function PnlCardActions(t0) {
  const $ = _c(28);
  const {
    onDeposit,
    onWithdraw,
    onGiftClickBefore
  } = t0;
  const t = useTranslations("profile.next.pnl_card");
  let t1;
  if ($[0] !== t) {
    t1 = t("deposit");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Icon icon={ArrowDown03Icon} className="size-5" aria-hidden="true" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("deposit");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <span>{t3}</span>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onDeposit || $[8] !== t1 || $[9] !== t4) {
    t5 = <AnimatedButton variant="primary" size="xl" className="grow basis-full sm:basis-0" onClick={onDeposit} aria-label={t1}>{t2}{t4}</AnimatedButton>;
    $[7] = onDeposit;
    $[8] = t1;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("withdraw");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon icon={ArrowUpRight01Icon} className="size-5" aria-hidden="true" />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t) {
    t8 = t("withdraw");
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <span>{t8}</span>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== onWithdraw || $[19] !== t6 || $[20] !== t9) {
    t10 = <AnimatedButton variant="secondary" size="xl" className="grow basis-0" onClick={onWithdraw} aria-label={t6}>{t7}{t9}</AnimatedButton>;
    $[18] = onWithdraw;
    $[19] = t6;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== onGiftClickBefore) {
    t11 = <GiftButton variant="secondary" size="xl" className="grow basis-0 sm:grow-0 sm:basis-auto" onClickBefore={onGiftClickBefore} />;
    $[22] = onGiftClickBefore;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t5) {
    t12 = <div className="flex flex-wrap gap-2">{t5}{t10}{t11}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t5;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Single-card portfolio hero. Renders Total + 24h PnL change +
 * Cash/Holdings/Positions tiles + Deposit/Withdraw/Gift buttons.
 */
export function PortfolioPnlCard(t0) {
  const $ = _c(110);
  const {
    wallets,
    registeredAt,
    viewerMode,
    userId,
    snapshotUserId,
    walletPubkeys
  } = t0;
  const t = useTranslations("profile.next.pnl_card");
  const tStats = useTranslations("profile.stats");
  const tPicker = useTranslations("profile.next.picker");
  const tWallet = useTranslations("wallet.table");
  const {
    setActiveWalletId
  } = useActiveWallet();
  const {
    enabled: multiWalletEnabled
  } = useMultiWalletFeature();
  const onrampEnabled = useFeatureGate("onramp_enabled") === true;
  const setSelection = usePortfolioWalletSelectionStore(_temp);
  let t1;
  if ($[0] !== wallets) {
    t1 = [...wallets];
    $[0] = wallets;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const resolved = useResolvedSelection(t1);
  let pickerTriggerLabel;
  let t2;
  if ($[2] !== resolved.mode || $[3] !== resolved.wallet || $[4] !== setSelection || $[5] !== t || $[6] !== tPicker || $[7] !== tWallet || $[8] !== wallets) {
    const activeWallets = wallets.filter(_temp2);
    const showWalletAddresses = activeWallets.length > 1;
    let t3;
    if ($[11] !== resolved.mode || $[12] !== resolved.wallet || $[13] !== t) {
      t3 = resolved.mode === "single" && resolved.wallet !== null ? resolved.wallet.label : t("total_label");
      $[11] = resolved.mode;
      $[12] = resolved.wallet;
      $[13] = t;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    pickerTriggerLabel = t3;
    const t4 = resolved.mode === "all" ? "success" : "muted";
    let t5;
    if ($[15] !== t4) {
      t5 = <PulseDot size="sm" color={t4} static={true} />;
      $[15] = t4;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== tPicker) {
      t6 = tPicker("all_wallets");
      $[17] = tPicker;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== t6) {
      t7 = <span className="truncate">{t6}</span>;
      $[19] = t6;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== t5 || $[22] !== t7) {
      t8 = <span className="flex items-center gap-2">{t5}{t7}</span>;
      $[21] = t5;
      $[22] = t7;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    let t9;
    if ($[24] !== setSelection) {
      t9 = () => {
        setSelection("all");
      };
      $[24] = setSelection;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[26] !== t8 || $[27] !== t9) {
      t10 = {
        label: t8,
        onSelect: t9
      };
      $[26] = t8;
      $[27] = t9;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    let t11;
    if ($[29] !== resolved.mode || $[30] !== resolved.wallet?.id || $[31] !== setSelection || $[32] !== showWalletAddresses || $[33] !== tWallet) {
      t11 = w_0 => {
        const isSelected = resolved.mode === "single" && resolved.wallet?.id === w_0.id;
        return {
          label: <span className="flex items-center gap-2"><PulseDot size="sm" color={isSelected ? "success" : "muted"} static={true} /><span className="truncate">{w_0.label}</span>{w_0.isAvailable === false ? <UnavailableWalletBadge label={tWallet("unavailable_on_device")} /> : null}{showWalletAddresses ? <span className="shrink-0 text-caption text-foreground-secondary">{truncateAddress(w_0.address)}</span> : null}</span>,
          onSelect: () => {
            setSelection(w_0.id);
          },
          disabled: w_0.isAvailable === false
        };
      };
      $[29] = resolved.mode;
      $[30] = resolved.wallet?.id;
      $[31] = setSelection;
      $[32] = showWalletAddresses;
      $[33] = tWallet;
      $[34] = t11;
    } else {
      t11 = $[34];
    }
    t2 = [t10, ...activeWallets.map(t11)];
    $[2] = resolved.mode;
    $[3] = resolved.wallet;
    $[4] = setSelection;
    $[5] = t;
    $[6] = tPicker;
    $[7] = tWallet;
    $[8] = wallets;
    $[9] = pickerTriggerLabel;
    $[10] = t2;
  } else {
    pickerTriggerLabel = $[9];
    t2 = $[10];
  }
  const pickerItems = t2;
  const queryPubkey = resolved.mode === "single" && resolved.wallet !== null ? resolved.wallet.address : userId;
  let t3;
  if ($[35] !== resolved.mode || $[36] !== resolved.wallet || $[37] !== walletPubkeys) {
    t3 = resolved.mode === "single" && resolved.wallet !== null ? [resolved.wallet.address] : walletPubkeys;
    $[35] = resolved.mode;
    $[36] = resolved.wallet;
    $[37] = walletPubkeys;
    $[38] = t3;
  } else {
    t3 = $[38];
  }
  const realtimeWalletPubkeys = t3;
  let t4;
  if ($[39] !== queryPubkey || $[40] !== realtimeWalletPubkeys) {
    t4 = {
      pubkey: queryPubkey,
      walletPubkeys: realtimeWalletPubkeys,
      range: "1d"
    };
    $[39] = queryPubkey;
    $[40] = realtimeWalletPubkeys;
    $[41] = t4;
  } else {
    t4 = $[41];
  }
  let t5;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      refetchInterval: false
    };
    $[42] = t5;
  } else {
    t5 = $[42];
  }
  const pnlResult = useGetUserPnlChart(t4, t5);
  useRefetchWhenVisible(pnlResult);
  const pnlData = pnlResult.data;
  const performance = pnlData?.portfolioPerformance ?? undefined;
  const t6 = performance?.line;
  let t7;
  if ($[43] !== t6) {
    t7 = adaptPerformanceSeries(t6);
    $[43] = t6;
    $[44] = t7;
  } else {
    t7 = $[44];
  }
  const series = t7;
  const t8 = performance?.deltaD6;
  let t9;
  if ($[45] !== t8) {
    t9 = signedAggregateD6Wire(t8);
    $[45] = t8;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  const deltaD6 = t9;
  const [qrOpen, setQrOpen] = useState(false);
  const [depositMounted, setDepositMounted] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [pnlOpen, setPnlOpen] = useState(false);
  let t10;
  if ($[47] !== resolved.mode || $[48] !== resolved.wallet || $[49] !== wallets) {
    t10 = function targetWallet() {
      if (resolved.mode === "single") {
        return resolved.wallet ?? undefined;
      }
      return wallets.find(_temp3);
    };
    $[47] = resolved.mode;
    $[48] = resolved.wallet;
    $[49] = wallets;
    $[50] = t10;
  } else {
    t10 = $[50];
  }
  const targetWallet = t10;
  let t11;
  if ($[51] !== setActiveWalletId || $[52] !== targetWallet) {
    t11 = function handleDeposit() {
      const target = targetWallet();
      if (target !== undefined) {
        setActiveWalletId(target.id);
      }
      setDepositMounted(true);
      setQrOpen(true);
    };
    $[51] = setActiveWalletId;
    $[52] = targetWallet;
    $[53] = t11;
  } else {
    t11 = $[53];
  }
  const handleDeposit = t11;
  let t12;
  if ($[54] !== setActiveWalletId || $[55] !== targetWallet) {
    t12 = function handleWithdraw() {
      const target_0 = targetWallet();
      if (target_0 !== undefined) {
        setActiveWalletId(target_0.id);
      }
      setTransferOpen(true);
    };
    $[54] = setActiveWalletId;
    $[55] = targetWallet;
    $[56] = t12;
  } else {
    t12 = $[56];
  }
  const handleWithdraw = t12;
  let t13;
  if ($[57] !== setActiveWalletId || $[58] !== targetWallet) {
    t13 = function handleGiftClickBefore() {
      const target_1 = targetWallet();
      if (target_1 !== undefined) {
        setActiveWalletId(target_1.id);
      }
    };
    $[57] = setActiveWalletId;
    $[58] = targetWallet;
    $[59] = t13;
  } else {
    t13 = $[59];
  }
  const handleGiftClickBefore = t13;
  let t14;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setPnlOpen(true);
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  let t15;
  if ($[61] !== t) {
    t15 = t("expand_pnl");
    $[61] = t;
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  let t16;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Icon icon={ExpandIcon} className="size-3.5" aria-hidden="true" />;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  let t17;
  if ($[64] !== pnlOpen || $[65] !== t15) {
    t17 = <AnimatedButton variant="tertiary" size="xs" onClick={t14} aria-label={t15} aria-haspopup="dialog" aria-expanded={pnlOpen} className="absolute right-3 top-3 z-10" data-testid="portfolio-pnl-card-expand">{t16}</AnimatedButton>;
    $[64] = pnlOpen;
    $[65] = t15;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  const t18 = series === null ? null : deltaD6;
  let t19;
  if ($[67] !== multiWalletEnabled || $[68] !== pickerItems || $[69] !== pickerTriggerLabel || $[70] !== realtimeWalletPubkeys || $[71] !== snapshotUserId || $[72] !== t18 || $[73] !== viewerMode) {
    t19 = <PortfolioCurrentSummary userId={snapshotUserId} walletPubkeys={realtimeWalletPubkeys} viewerMode={viewerMode} pickerTriggerLabel={pickerTriggerLabel} pickerItems={pickerItems} showWalletPicker={multiWalletEnabled} deltaD6={t18} deltaPercentTenths={null} deltaFullWindow={true} />;
    $[67] = multiWalletEnabled;
    $[68] = pickerItems;
    $[69] = pickerTriggerLabel;
    $[70] = realtimeWalletPubkeys;
    $[71] = snapshotUserId;
    $[72] = t18;
    $[73] = viewerMode;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  let t20;
  if ($[75] !== deltaD6 || $[76] !== pnlResult.isPending || $[77] !== series) {
    t20 = pnlResult.isPending ? <Skeleton className="col-start-2 row-start-1 h-20 min-h-20 w-full min-w-0 overflow-hidden rounded-card" data-testid="portfolio-pnl-chart-loading" /> : series !== null ? <div className="col-start-2 row-start-1 h-20 min-h-20 w-full min-w-0 overflow-hidden" data-testid="portfolio-pnl-card-chart-wrapper"><PnlCardLiveline series={series} tone={(deltaD6 ?? BigInt(0)) >= BigInt(0) ? "success" : "destructive"} /></div> : null;
    $[75] = deltaD6;
    $[76] = pnlResult.isPending;
    $[77] = series;
    $[78] = t20;
  } else {
    t20 = $[78];
  }
  let t21;
  if ($[79] !== handleDeposit || $[80] !== handleGiftClickBefore || $[81] !== handleWithdraw || $[82] !== viewerMode) {
    t21 = viewerMode === "own" ? <div className="col-span-2 row-start-3"><PnlCardActions onDeposit={handleDeposit} onWithdraw={handleWithdraw} onGiftClickBefore={handleGiftClickBefore} /></div> : null;
    $[79] = handleDeposit;
    $[80] = handleGiftClickBefore;
    $[81] = handleWithdraw;
    $[82] = viewerMode;
    $[83] = t21;
  } else {
    t21 = $[83];
  }
  let t22;
  if ($[84] !== t19 || $[85] !== t20 || $[86] !== t21) {
    t22 = <PanelContent className="flex-1"><div className="grid h-full min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-4" data-testid="portfolio-pnl-card-hero-grid">{t19}{t20}{t21}</div></PanelContent>;
    $[84] = t19;
    $[85] = t20;
    $[86] = t21;
    $[87] = t22;
  } else {
    t22 = $[87];
  }
  let t23;
  if ($[88] !== t17 || $[89] !== t22) {
    t23 = <Panel className="relative h-full">{t17}{t22}</Panel>;
    $[88] = t17;
    $[89] = t22;
    $[90] = t23;
  } else {
    t23 = $[90];
  }
  let t24;
  if ($[91] !== depositMounted || $[92] !== onrampEnabled || $[93] !== qrOpen || $[94] !== transferOpen || $[95] !== viewerMode) {
    t24 = viewerMode === "own" ? <>{onrampEnabled ? depositMounted ? <FundingEntry open={qrOpen} onClose={() => setQrOpen(false)} /> : null : qrOpen ? <WalletQrDialog open={qrOpen} onOpenChange={setQrOpen} /> : null}<FundingEntry mode="withdraw" open={transferOpen} onClose={() => setTransferOpen(false)} /></> : null;
    $[91] = depositMounted;
    $[92] = onrampEnabled;
    $[93] = qrOpen;
    $[94] = transferOpen;
    $[95] = viewerMode;
    $[96] = t24;
  } else {
    t24 = $[96];
  }
  let t25;
  if ($[97] !== tStats) {
    t25 = tStats("pnl_chart_title");
    $[97] = tStats;
    $[98] = t25;
  } else {
    t25 = $[98];
  }
  let t26;
  if ($[99] !== queryPubkey || $[100] !== registeredAt) {
    t26 = <PnlChartCard pubkey={queryPubkey} registeredAt={registeredAt} hideTitle={true} />;
    $[99] = queryPubkey;
    $[100] = registeredAt;
    $[101] = t26;
  } else {
    t26 = $[101];
  }
  let t27;
  if ($[102] !== pnlOpen || $[103] !== t25 || $[104] !== t26) {
    t27 = <SilkSheet open={pnlOpen} onOpenChange={setPnlOpen} variant="detached" size="xl" title={t25}>{t26}</SilkSheet>;
    $[102] = pnlOpen;
    $[103] = t25;
    $[104] = t26;
    $[105] = t27;
  } else {
    t27 = $[105];
  }
  let t28;
  if ($[106] !== t23 || $[107] !== t24 || $[108] !== t27) {
    t28 = <>{t23}{t24}{t27}</>;
    $[106] = t23;
    $[107] = t24;
    $[108] = t27;
    $[109] = t28;
  } else {
    t28 = $[109];
  }
  return t28;
}
function _temp3(w_1) {
  return w_1.archivedAt === null;
}
function _temp2(w) {
  return w.archivedAt === null;
}
function _temp(s) {
  return s.setSelection;
}